import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Clock, Check, RotateCcw, Trophy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Header from "../components/Header";
import TutorialVideoSection from "../components/TutorialVideoSection";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { localStartOfWeek, localStartOfMonth } from "../lib/timeWindow";
import PersonalBestBadge from "../components/PersonalBestBadge";
import AchievementUnlocked from "../components/AchievementUnlocked";
import RatingSummary from "../components/RatingSummary";
import { targetPaceLine } from "../lib/targetPace";
import { toast } from "sonner";

const PHASES = {
  CONFIG: "config",
  MEMORIZE: "memorize",
  RECALL: "recall",
  RESULTS: "results",
};

const DIFFICULTIES = [
  { value: "simple", label: "Simple", description: "Concrete nouns - instantly visual (sword, tiger)" },
  { value: "medium", label: "Medium", description: "Abstract concepts - harder to visualise (freedom, collapse)" },
  { value: "hard", label: "Hard", description: "Adjectives & qualities - no obvious form (relentless, charisma)" },
  { value: "complex", label: "Complex", description: "Uncommon but genuine words (ephemeral, labyrinthine)" },
];

const WordTraining = ({ user }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(PHASES.CONFIG);
  const [wordCount, setWordCount] = useState(10);
  const [difficulty, setDifficulty] = useState("simple");
  const [memorizeTime, setMemorizeTime] = useState(60);
  const [sessionData, setSessionData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [memorizeSeconds, setMemorizeSeconds] = useState(0);
  const timerRef = useRef(null);
  const recallTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recallTimerRef.current) clearInterval(recallTimerRef.current);
    };
  }, []);

  const startTraining = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/training/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "words",
          items_count: wordCount,
          difficulty: difficulty,
        }),
      });

      if (!response.ok) throw new Error("Failed to start training");

      const data = await response.json();
      setSessionData(data);
      setUserAnswers(data.items.map(() => ""));
      setTimeLeft(memorizeTime);
      setPhase(PHASES.MEMORIZE);

      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setMemorizeSeconds(memorizeTime);
            setPhase(PHASES.RECALL);
            setElapsedTime(0);
            recallTimerRef.current = setInterval(() => {
              setElapsedTime((t) => t + 1);
            }, 1000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      toast.error("Failed to start training");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...userAnswers];
    newAnswers[index] = value;
    setUserAnswers(newAnswers);
  };

  const submitAnswers = async () => {
    if (recallTimerRef.current) clearInterval(recallTimerRef.current);
    setLoading(true);

    try {
      const response = await authFetch(`${API}/training/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionData.session_id,
          answers: userAnswers,
          time_taken_seconds: elapsedTime,
          memorize_seconds: memorizeSeconds,
          start_of_week_iso: localStartOfWeek(),
          start_of_month_iso: localStartOfMonth(),
        }),
      });

      if (!response.ok) throw new Error("Failed to submit");

      const data = await response.json();
      setResults(data);
      setPhase(PHASES.RESULTS);
    } catch (error) {
      toast.error("Failed to submit answers");
    } finally {
      setLoading(false);
    }
  };

  const resetTraining = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recallTimerRef.current) clearInterval(recallTimerRef.current);
    setPhase(PHASES.CONFIG);
    setSessionData(null);
    setUserAnswers([]);
    setResults(null);
    setElapsedTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="word-training-page">
      <Header user={user} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          {/* Config Phase */}
          {phase === PHASES.CONFIG && (
            <motion.div
              key="config"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Configure Training</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  {/* Difficulty */}
                  <div className="space-y-4">
                    <Label className="text-lg">Difficulty</Label>
                    <Select value={difficulty} onValueChange={setDifficulty}>
                      <SelectTrigger className="w-full" data-testid="difficulty-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DIFFICULTIES.map((d) => (
                          <SelectItem key={d.value} value={d.value}>
                            <div>
                              <div className="font-medium">{d.label}</div>
                              <div className="text-xs text-muted-foreground">{d.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Word Count */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-lg">Number of Words</Label>
                      <span className="font-mono text-2xl text-secondary">{wordCount}</span>
                    </div>
                    <Slider
                      value={[wordCount]}
                      onValueChange={(v) => setWordCount(v[0])}
                      min={5}
                      max={50}
                      step={5}
                      className="w-full"
                      data-testid="word-count-slider"
                    />
                  </div>

                  {/* Memorize Time */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-lg">Memorization Time</Label>
                      <span className="font-mono text-2xl text-secondary">{memorizeTime}s</span>
                    </div>
                    <Slider
                      value={[memorizeTime]}
                      onValueChange={(v) => setMemorizeTime(v[0])}
                      min={30}
                      max={300}
                      step={30}
                      className="w-full"
                      data-testid="memorize-time-slider"
                    />
                    <p className="text-xs text-muted-foreground" data-testid="target-pace-line">
                      {targetPaceLine("words", wordCount, "words")}
                    </p>
                  </div>

                  <Button
                    onClick={startTraining}
                    disabled={loading}
                    className="w-full bg-secondary text-secondary-foreground py-6 text-lg btn-hover hover:bg-secondary/90"
                    data-testid="start-training-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-secondary-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Play className="w-5 h-5 mr-2" />
                        Start Training
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
              <TutorialVideoSection section="words" />
            </motion.div>
          )}

          {/* Memorize Phase */}
          {phase === PHASES.MEMORIZE && sessionData && (
            <motion.div
              key="memorize"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-2xl">Memorize</CardTitle>
                  <div className="flex items-center gap-2 text-secondary">
                    <Clock className="w-5 h-5" />
                    <span className="font-mono text-2xl">{formatTime(timeLeft)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-4 text-center font-mono uppercase tracking-widest">
                    Read in order: left → right, top → bottom
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
                    {sessionData.items.map((word, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="relative flex items-center gap-3 p-4 bg-muted border border-border"
                        data-testid={`memorize-word-${index}`}
                      >
                        <span className="font-mono text-sm text-secondary/80 font-bold tabular-nums w-7 shrink-0 text-right">
                          {index + 1}.
                        </span>
                        <span className="text-lg font-medium flex-1 text-center pr-7">{word}</span>
                      </motion.div>
                    ))}
                  </div>

                  <Button
                    onClick={() => {
                      clearInterval(timerRef.current);
                      setMemorizeSeconds(memorizeTime - timeLeft);
                      setPhase(PHASES.RECALL);
                      setElapsedTime(0);
                      recallTimerRef.current = setInterval(() => {
                        setElapsedTime((t) => t + 1);
                      }, 1000);
                    }}
                    variant="outline"
                    className="w-full py-6"
                    data-testid="skip-to-recall-btn"
                  >
                    I'm Ready - Skip to Recall
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Recall Phase */}
          {phase === PHASES.RECALL && sessionData && (
            <motion.div
              key="recall"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-2xl">Recall</CardTitle>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-5 h-5" />
                    <span className="font-mono text-xl">{formatTime(elapsedTime)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-6">
                    Enter the words you memorized in order.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {userAnswers.map((answer, index) => (
                      <div key={index} className="space-y-2">
                        <Label className="text-sm text-muted-foreground">
                          Word {index + 1}
                        </Label>
                        <Input
                          value={answer}
                          onChange={(e) => handleAnswerChange(index, e.target.value)}
                          placeholder="Enter word..."
                          className="bg-muted border-border"
                          data-testid={`recall-input-${index}`}
                        />
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={submitAnswers}
                    disabled={loading}
                    className="w-full bg-secondary text-secondary-foreground py-6 text-lg btn-hover hover:bg-secondary/90"
                    data-testid="submit-answers-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-secondary-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Submit Answers
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Results Phase */}
          {phase === PHASES.RESULTS && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-secondary" />
                    Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* NPB badge */}
                  <div className="text-center">
                    <PersonalBestBadge results={results} />
                  </div>
                  <AchievementUnlocked results={results} />
                  {/* Rating */}
                  <RatingSummary
                    results={results}
                    mode="words"
                    itemsCount={wordCount}
                    difficulty={difficulty}
                    memorizeAllowanceSeconds={memorizeTime}
                    accent="text-secondary"
                  />

                  {/* Time Breakdown */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="text-center p-3 bg-muted border border-border rounded">
                      <div className="text-xl font-mono text-purple-400">{formatTime(memorizeSeconds)}</div>
                      <p className="text-xs text-muted-foreground mt-1">Memorisation</p>
                    </div>
                    <div className="text-center p-3 bg-muted border border-border rounded">
                      <div className="text-xl font-mono text-blue-400">{formatTime(Math.round(results.time_taken_seconds))}</div>
                      <p className="text-xs text-muted-foreground mt-1">Recall</p>
                    </div>
                  </div>

                  {/* Comparison */}
                  <div className="space-y-4 mb-8">
                    <h3 className="font-semibold text-lg">Comparison</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {results.original_items.map((original, index) => {
                        const userAnswer = results.user_answers[index] || "";
                        // Backend now returns per-item credit (1.0 exact, 0.5 near-miss, 0.0 wrong).
                        // Fall back to a naive case-insensitive equality if credits are missing (older backend).
                        const c = results.item_credits?.[index];
                        const credit = c ?? (original.toLowerCase() === userAnswer.toLowerCase() ? 1.0 : 0.0);
                        const isCorrect = credit === 1.0;
                        const isClose = credit === 0.5;
                        return (
                          <div
                            key={index}
                            className={`p-4 border ${
                              isCorrect
                                ? "border-green-500/50 bg-green-500/10"
                                : isClose
                                ? "border-amber-500/50 bg-amber-500/10"
                                : "border-destructive/50 bg-destructive/10"
                            }`}
                            data-testid={`comparison-${index}`}
                          >
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm text-muted-foreground">#{index + 1}</span>
                              {isCorrect ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : isClose ? (
                                <span className="text-amber-500 text-xs font-mono">≈ ½</span>
                              ) : (
                                <span className="text-destructive text-xs">✗</span>
                              )}
                            </div>
                            <div className="font-medium">{original}</div>
                            {!isCorrect && userAnswer && (
                              <div className={`text-sm mt-1 ${isClose ? "text-amber-500" : "text-destructive"}`}>
                                Your answer: {userAnswer}
                                {isClose && <span className="ml-1 text-xs opacity-80">· close, half credit</span>}
                              </div>
                            )}
                            {!isCorrect && !userAnswer && (
                              <div className="text-sm text-muted-foreground mt-1 italic">
                                No answer
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4">
                    <Button
                      onClick={resetTraining}
                      className="flex-1 bg-secondary text-secondary-foreground py-6 btn-hover hover:bg-secondary/90"
                      data-testid="try-again-btn"
                    >
                      <RotateCcw className="w-5 h-5 mr-2" />
                      Try Again
                    </Button>
                    <Button
                      onClick={() => navigate("/leaderboard")}
                      variant="outline"
                      className="flex-1 py-6"
                      data-testid="view-leaderboard-btn"
                    >
                      <Trophy className="w-5 h-5 mr-2" />
                      Leaderboard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default WordTraining;
